<template>
  <div class="card">

    <div class="bg-blue-light p-1">
      <TitleButton
        :showBtn="true"
        :showAddNew="false"
        title="Pharmacy Bill"
        @onClickCloseButton="goToList"
      >
        <template #extra-btn v-if="!!productList.length">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary position-relative"
            @click="handleModalOpen"
          >
            <i class="fa-solid fa-bell"></i>

            <span
              class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
            >
              {{ productList.length }}
            </span>
          </button>
        </template>
      </TitleButton>
    </div>

    <div class="my-2 px-2">
      <TableHeaderData
        :formData="formData"
        :tableItems="tableItems"
        :additionalProductQuery="additionalProductQuery"
        ref="formHeader"
        @update-item-quantity-on-cache="updateItemQuantityOnCache"
      />
    </div>

    <div class="col-12 px-2">
      <ListTable
        :tableItems="tableItems"
        @addNewItem="addNewItem"
        @onUpdateQuantity="updateItemQuantityOnCache"
        @onDeleteItem="deleteItemFromCache"
        :salesAccountHeads="salesAccountHeads"
        :vatRate="vatRate"
        :isHideAddBtn="true"
      />
    </div>
    <div class="row px-2">
      <div class="col-md-6">
        <div class="mb-1">
          <label class="form-label" for="description">Ledger Memo</label>
          <vField
            as="textarea"
            name="description"
            type="number"
            class="form-control"
            v-model="formData.description"
          />
        </div>
      </div>
      <div class="col-md-5 ml-auto mt-auto mb-1">
        <div class="d-flex flex-wrap gap-1 gy-2">
          <button :disabled="submitLoading" @click="handleSubmit(false)" class="btn btn-primary">Save</button>
          <button :disabled="submitLoading" @click="handleSubmit(true)" class="btn btn-primary">Save & Print</button>
        </div>
      </div>
    </div>

    <ProductsModal v-if="isModalOpen" :product-list="productList" @update-product-list="updateProductList" />

    <Loader v-if="loader"/>
  </div>
</template>

<script setup>
import {computed, inject, onMounted, provide, ref} from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import ListTable from '@/components/molecule/company/hospital/general-billing/v2/InvoiceHospitalTable.vue';
import TableHeaderData from '@/components/atom/hospital/pharmacy-bill-pos/TableHeaderInvoiceHospital.vue';
import {useRoute, useRouter} from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import {vatRate} from '@/data/inventory.js';
import handleCompany from "@/services/modules/company";
import hospitalPdfPrinterHelper from '@/services/utils/hospitalPdfPrinterHelper';
import useDate from "@/services/utils/day";
import { watch } from 'vue';
import Loader from '@/components/atom/LoaderComponent'
import ProductsModal from "@/components/atom/hospital/pharmacy-bill-pos/ProductsModal.vue";
import {useStore} from "vuex";

const { generatePdf } = hospitalPdfPrinterHelper();
const { fetchSalesBill, getProductsFormKey, cacheProductQuantity, removeProductsFormKey, ...rest } = handlePurchase();
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const date = useDate();
const customizationPdfFields = ref([]);

const companyId = $route.params.companyId;
const loader = ref(true);
const submitLoading = ref(false);
const formHeader = ref(null);
const salesAccountHeads = ref([]);
const totals = ref({});
const invoiceRes = ref({});
const company = ref({});
const template = ref(null);
const productList = ref([]);
let formData = ref({
  company_id: companyId,
  contact_profile_id: null,
  referrer_id: null,
  account_head_id: null,
  ipd_register_id: null,
  vat_payable_account_head_id: null,
  project_id: null,
  cost_centre_id: null,
  location_id: null,
  mop_account_head_id: null,
  discount_head_id: null,
  service_resource_id: null,
  receipt_reference: null,
  adjustment_amount: null,
  receipt_description: "Pharmacy bill payment",
  payment: null,
  status: 'active',
  date: '',
  has_item_detail: true,
  description: '',
  order_number: null,
  sales_person_id: null,
  commission_percent: null,
  lc_number: null,
  item_details: []
});
const formKey = computed(() => $route.query.form_key ?? null);
const isModalOpen = computed(() => $store.state.isModalOpen);

provide('formData', formData);
provide('totals', totals);
const showSuccess =  inject('showSuccess');
const showError =  inject('showError');

const additionalProductQuery = computed(() => {
  return {
    location_id: formData.value.location_id,
    with_desc_stock: 1,
    form_key: formKey.value
  }
})

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${companyId}&customization_type=pdf&type=pharmacy_bill_pdf`;
  const res = await fetchActiveCustomizationField(companyQuery);
  customizationPdfFields.value = res.data;
};

const updateProductList = (pList) => {
    productList.value = pList;
};

const handleModalOpen = async () => {
  $store.state.isModalOpen = true;
};

const getFormReservedProducts = async () => {
  if (formKey.value) {
    const companyQuery = `?company_id=${companyId}`;
    const res = await getProductsFormKey(formKey.value, companyQuery);
    productList.value = res.data ?? [];
  }
};

const updateItemQuantityOnCache = async (data) => {
  if (formKey.value) {
    data.form_key = formKey.value;
  }

  const res = await cacheProductQuantity(data);

  if ((res.data && res.data.form_key)) {
    await $router.push({
      query: {...$route.query, form_key: res.data.form_key}
    })
  }
}

const deleteItemFromCache = (item) => {
  const query = `?product_id=${item.product_id}`;
  removeProductsFormKey(formKey.value, query);
}

onMounted(() => {
  loader.value=true
  const companyQuery = `?company_id=${companyId}`;
  Promise.all([
    rest.fetchAccountReceivable(companyQuery).then(res=> {
      if(res.data) formData.value.account_head_id = res.data[0].id;
    }),
    rest.fetchVatPayable(companyQuery).then(res=> {
      if(res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
    }),
    fetchCompanyInfo(companyId).then(res=> {
      if(res.data) company.value = res.data;
    }),
    rest.getAccountHeadBySlag("discount_account", companyQuery).then(res => {
      if(res.data) {
        salesAccountHeads.value = res.data
      }
    }),
      getActiveFiledCustomization(),
      getFormReservedProducts(),
  ])
    .finally(() => {loader.value=false})
})

let tableItems = ref([]);

const addNewItem = () => {
  tableItems.value.push({
    product_id: null,
    name: '',
    description: '',
    quantity : 1,
    rate: 0,
    purchase_rate: 0,
    discount_percent: 0,
    vat: 0,
    sub_total: 0,
    discount_amount: 0,
    vat_amount: 0,
    total: 0,
    is_edit: false,
  });
}

const goToList = () => {
  $router.push({name: 'pharmacy-bill-list',
    params: {
      companyId: $route.params.companyId,
      moduleId: $route.params.moduleId,
      menuId: $route.params.menuId,
      pageId: $route.params.pageId
    },
    query: {
      start: $route.query.start,
      end: $route.query.end
    }

  })
}

const resetForm = () => {
  const accountHeadId = formData.value.account_head_id
  const vatPayableAccountHeadId = formData.value.vat_payable_account_head_id
  formData.value = {
    company_id: companyId,
    contact_profile_id: null,
    referrer_id: null,
    ipd_register_id: null,
    project_id: null,
    account_head_id: accountHeadId,
    cost_centre_id: null,
    location_id: null,
    mop_account_head_id: null,
    vat_payable_account_head_id: vatPayableAccountHeadId,
    discount_head_id: null,
    service_resource_id: null,
    receipt_reference: null,
    adjustment_amount: null,
    receipt_description: "Pharmacy bill payment",
    payment: null,
    status: 'active',
    date: '',
    has_item_detail: true,
    description: '',
    order_number: null,
    sales_person_id: null,
    commission_percent: null,
    lc_number: null,
    item_details: []
  };
  tableItems.value = [];
}

const clearProductCache = async () => {
  const query = `?is_delete_all=1`;
  await removeProductsFormKey(formKey.value, query);
  const routeQuery = { ...$route.query }
  delete routeQuery.form_key
  await $router.replace({ query: routeQuery })

}

const handleSubmit = (print=false) => {
  submitLoading.value = true;
  if(!formData.value.date) formData.value.date = date.currentDate();
  formData.value.item_details = tableItems;
  let copyFormData = Object.assign({}, formData.value);
  copyFormData.item_details = formData.value.item_details
  copyFormData.sale_type = "pharmacy_invoice"
  copyFormData.receipt_type = "pharmacy_invoice"

  rest.storeBillPayment(copyFormData)
    .then(res => {
      if(res.status) {
        submitLoading.value = false;
        tableItems.value = [];
        resetForm();
        clearProductCache();
        showSuccess(res.message)
        if(print) goToPrint(res.data.id)
        if (formHeader.value) formHeader.value.resetData()
        return;
      }
      showError(res.message)
      submitLoading.value = false;
    })
    .catch(err=> {
      showError(err.response.data.message)
      submitLoading.value = false;
    })
}

const goToPrint = (id) => {
  const companyQuery = `?company_id=${companyId}`;

  fetchSalesBill(id, companyQuery).then( (res) =>{
    if( res ){
      invoiceRes.value = res.data;
    }
  }).then( () => {
    generatePdf(company.value, invoiceRes.value, 'BILL', true, true, false, false, customizationPdfFields.value && customizationPdfFields.value.heading_left_pos_print, customizationPdfFields.value)
  }).catch( (err) => {
    console.log(err);
  })
}

const getProduct = (item) => {
  if(! item.product) return null;

  return {
    id: item.product.id,
    product_id: item.product.id,
    name: item.product.name,
    quantity: item.quantity,
    stock_in_hand: item.stock_in_hand,
    rate:  item.rate,
    amount:  item.amount,
    vat: item.vat,
    vat_amount:  item.vat_amount,
    discount_percent: item.discount_percent,
    discount_amount: item.discount_amount,
    description: item.product.description && item.product.description.unit ? item.product.description.unit.name : '',
    is_edit: false
  }
}

const setTableItems = (invoice) => {
  for(const item of invoice.general) {
    const productDetails = getProduct(item);
    if(! productDetails) continue;

    tableItems.value.push(productDetails);
  }
}

const getSingleSalesBill = async () => {

  const id = template.value && template.value.templateable_id;
  if(!id) return;

  const companyQuery = `?company_id=${companyId}`;
  loader.value = true;

  await fetchSalesBill(id, companyQuery).then( (res) =>{
    if(! res.data) return;
    setTableItems(res.data);
  }).catch( (err) => {
    console.log(err);
  }).finally(() => {
    loader.value = false;
  })
}

watch(template, (newValue, oldValue) => {
  if(newValue) {
    getSingleSalesBill();
  }
});

</script>
<style scoped>
address{
  margin-bottom: 0 !important;
}
.template-selection {
  margin-left: -97%;
  width: 166%;
}
</style>
